import React from 'react';
import { useTranslation } from 'react-i18next';

import { Logout } from '@app/components/Logout/Logout';
import { reloadPage } from '@app/helpers/navigation';
import { NAMESPACE } from '@app/i18n';
import { Button } from '@chakra-primitives/button';

interface ErrorMessageProps {
  /** The error message to display to the user */
  message: string;
  /** The user's login ID for support purposes */
  loginId: string | undefined;
  /**
   * When true, show a "Try again" button. Used for transient failures where
   * the session is still valid so the user isn't stranded on a dead-end screen
   * with only "Back to Login".
   */
  canRetry?: boolean;
  /**
   * Handler for the "Try again" button. Prefer an in-app retry (re-run bootstrap)
   * over a full-document reload so a still-offline retry re-renders this screen
   * rather than a browser error page. Falls back to window.location.reload().
   */
  onRetry?: () => void;
}

/**
 * ErrorMessage component displays authentication and loading errors.
 *
 * Shows the error message, user ID for support purposes, and provides
 * a logout option for the user to try again.
 */
export const ErrorMessage: React.FC<ErrorMessageProps> = ({
  message,
  loginId,
  canRetry = false,
  onRetry,
}) => {
  const { t } = useTranslation(NAMESPACE.APP_SHELL);

  return (
    <div className="text-container">
      {/* eslint-disable-next-line react/jsx-no-literals -- reason: runtime error message from props, not a static UI literal */}
      <div className="connectingText error">{`${message}\n`}</div>
      <div>{t('routes.userId', { loginId })}</div>
      <div>{t('routes.contactSupport')}</div>
      {canRetry && (
        <Button
          variant="outline"
          onClick={() => (onRetry ? onRetry() : reloadPage())}
        >
          {t('routes.tryAgain')}
        </Button>
      )}
      <Logout />
    </div>
  );
};
